import { mergeClasses, Spinner } from '@fluentui/react-components';
import { isMobile } from 'react-device-detect';
import { observer } from 'mobx-react-lite';

import EnhancedAvatar from 'src/components/atoms/enhanced-avatar/EnhancedAvatar';
import Logo from 'src/components/atoms/logo/Logo';
import EnhancedExternalLink from 'src/components/atoms/enhanced-external-link/EnhancedExternalLink';
import { useStore } from 'src/hooks/useStore';
import { useDeviceIcons } from 'src/hooks/useDeviceIcons';
import { useInterestIcons } from 'src/hooks/useInterestIcons';
import IconGroupSection from 'src/components/molecules/icon-group-section/IconGroupSection';
import { ImageIcon } from 'src/ui-models/Icon';
import { useHeaderStyles } from './Header.styles';

const Header: React.FC = () => {
  const { profileStore } = useStore();
  const { isLoadingDevices, deviceIcons, loadDevicesError } = useDeviceIcons();
  const { isLoadingInterests, interestIcons, loadInterestsError } = useInterestIcons();
  const styles = useHeaderStyles();
  const profile = profileStore.profile;
  const logoUrl = profile?.logoUrl ?? '';
  const avatarSize = isMobile ? 72 : 96;

  const viewFullWebTitle = 'View full web experience';
  const devicesAndAppsTitle = 'DEVICES/APPS';
  const interestsTitle = 'INTERESTS';
  const avatarAriaLabel = 'Avatar of user';
  const viewFullWebLink = 'https://www.google.com/';

  if (loadDevicesError || loadInterestsError) {
    throw loadDevicesError || loadInterestsError;
  }

  const getIconGroupSection = (isLoading: boolean, title: string, items: ImageIcon[]): React.ReactNode => {
    return isLoading ? <Spinner /> : <IconGroupSection title={title} items={items} />;
  };

  return (
    <div className={mergeClasses('mashead-container', styles.container)}>
      <div className={styles.row}>
        <Logo src={logoUrl} />
      </div>

      <div className={styles.row}>
        {getIconGroupSection(isLoadingDevices, devicesAndAppsTitle, deviceIcons)}

        <EnhancedAvatar
          name={profile?.displayName ?? ''}
          className={styles.avatar}
          image={{ src: profile?.profileImageUrl ?? '' }}
          initials={profile?.displayNameInitials}
          aria-label={avatarAriaLabel}
          color="colorful"
          size={avatarSize}
        />

        {getIconGroupSection(isLoadingInterests, interestsTitle, interestIcons)}
      </div>

      <div className={styles.row}>
        <EnhancedExternalLink href={viewFullWebLink}>{viewFullWebTitle}</EnhancedExternalLink>
      </div>
    </div>
  );
};

export default observer(Header);
